import { UNIT_H, UNIT_W } from './CombatUnit';

// Pure geometry for the combat stage: how big the unit tiles render and
// where the two rows sit on the canvas.
//
// Extracted from CombatCanvas so it can be tested without a PIXI context.
// It is the piece that broke in portrait: the fit used to consider WIDTH
// only, so on a tall canvas the two rows were pinned to the top and bottom
// edges with a large dead band between them, and on a short landscape
// canvas the tiles could overflow vertically.

export const SIDE_PAD = 8;
export const BASE_SCALE = 1.5;
export const BASE_GAP = 10;

/** Portrait tiles may scale UP into spare height, but only this far —
    beyond it the procedural art reads as blurry rather than big. */
const MAX_PORTRAIT_UPSCALE = 1.6;

export interface StageLayout {
  /** Final scale applied to every unit (both rows share it). */
  unifiedScale: number;
  tileW: number;
  tileH: number;
  gap: number;
  /** Top edge Y of each row. */
  enemyTopY: number;
  playerTopY: number;
  /** Vertical space between the two rows. */
  midGap: number;
}

export function computeStageLayout(
  width: number,
  height: number,
  playerCount: number,
  enemyCount: number,
): StageLayout {
  // Unified scale: both rows render at the same tile size, driven by
  // whichever side has more cards. Prevents the "player cards 2× the size
  // of enemy cards" mismatch.
  const maxCount = Math.max(playerCount, enemyCount, 1);
  const baseTotalW =
    maxCount * UNIT_W * BASE_SCALE +
    Math.max(0, maxCount - 1) * BASE_GAP * BASE_SCALE;

  const availableW = width - SIDE_PAD * 2;
  const widthScale = baseTotalW > availableW ? availableW / baseTotalW : 1;

  const isPortraitCanvas = height > width * 1.2;
  const vertPad = Math.max(12, height * 0.04);
  // Breathing room between the rows so the battle line reads as a
  // confrontation rather than two unrelated strips.
  const midGap = Math.max(24, height * 0.1);
  const availableH = height - vertPad * 2 - midGap;
  const heightScale = availableH / (2 * UNIT_H * BASE_SCALE);

  const fitScale = Math.min(
    widthScale,
    heightScale,
    isPortraitCanvas ? MAX_PORTRAIT_UPSCALE : 1,
  );

  const unifiedScale = BASE_SCALE * fitScale;
  const tileH = UNIT_H * unifiedScale;

  return {
    unifiedScale,
    tileW: UNIT_W * unifiedScale,
    tileH,
    gap: BASE_GAP * unifiedScale,
    // Rows straddle the vertical centre line rather than the canvas edges.
    enemyTopY: height / 2 - midGap / 2 - tileH,
    playerTopY: height / 2 + midGap / 2,
    midGap,
  };
}
